import './SetPassword.css';
import Header from 'components/Header/Header';
import LoadingButton from 'components/LoadingButton/LoadingButton';
import { useEffect, useState } from 'react';
import { Alert, Button, Card, Col, Form, Row, Spinner } from 'react-bootstrap';
import { Link, useLocation } from 'react-router-dom';
import { isPasswordValid } from 'utils/validation.util';
import useToken from 'components/useToken';
import User from 'utils/user.util';
import { useTranslation } from 'react-i18next';

const ResetPassword = () => {
    const { t } = useTranslation();
    const { setToken } = useToken();
    const url = useLocation();
    const token = new URLSearchParams(url.search).get('token');

    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [alert, setAlert] = useState(null);
    const [isValidatingToken, setIsValidatingToken] = useState(true);
    const [isTokenValid, setIsTokenValid] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [showResendActivationEmailBtn, setShowResendActivationEmailBtn] = useState(false);

    const [validated, setValidated] = useState(false);
    const [passwordError, setPasswordError] = useState('');
    const [confirmPasswordError, setConfirmPasswordError] = useState('');
    const [passwordReset, setPasswordReset] = useState(false);

    const isResetingPassword = url.pathname === '/resetpassword';

    const validate = () => {
        setAlert(null);
        setPasswordError('');
        setConfirmPasswordError('');

        if (!password || !isPasswordValid(password)) {
            setPasswordError('password_validation_error');
            setValidated(true);
            return false;
        } else if (password !== confirmPassword) {
            setConfirmPasswordError('password_confirm_error');
            setValidated(true);
            return false;
        }
        return true;
    };

    const resendUserActivationEmail = () => {
        fetch(`${process.env.REACT_APP_PROXY_URL || ''}/sendactivationemail`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                token,
            }),
        })
            .then((data) => data.json())
            .then((data) => {
                if (data.status === 200) {
                    setAlert({
                        type: 'success',
                        message: 'activation_email_sent',
                    });
                } else {
                    setAlert({
                        type: 'danger',
                        message: isResetingPassword
                            ? 'reset_password_token_invalid'
                            : 'user_activation_token_invalid',
                    });
                }
                setShowResendActivationEmailBtn(false);
            })
            .catch((error) => {
                console.log({ error });
            });
    };

    const setNewPassword = (event) => {
        event.preventDefault();
        if (validate()) {
            setIsLoading(true);
            fetch(`${process.env.REACT_APP_PROXY_URL || ''}/updatepassword`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    token,
                    password,
                }),
            })
                .then((data) => data.json())
                .then((data) => {
                    if (data.status === 200) {
                        setAlert({
                            type: 'success',
                            message: isResetingPassword
                                ? 'password_update_success'
                                : 'user_activation_success',
                        });
                        setPasswordReset(true);
                    } else {
                        setAlert({
                            type: 'danger',
                            message: isResetingPassword
                                ? 'password_update_fail'
                                : 'user_activation_fail',
                        });
                    }
                    setIsLoading(false);
                })
                .catch((error) => {
                    console.log({ error });
                    setIsLoading(false);
                    setAlert({
                        type: 'danger',
                        message: isResetingPassword
                            ? 'password_update_fail'
                            : 'user_activation_fail',
                    });
                });
        }
    };

    useEffect(() => {
        setToken(null);
        User.clear();

        // validating the JWT token
        if (token) {
            fetch(`${process.env.REACT_APP_PROXY_URL || ''}/validatepasswordtoken`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    token,
                }),
            })
                .then((data) => data.json())
                .then((data) => {
                    if (data.status === 403) {
                        setAlert({
                            type: 'danger',
                            message: isResetingPassword
                                ? 'reset_password_token_invalid'
                                : 'user_activation_token_invalid',
                        });
                    } else if (data.status === 401) {
                        setAlert({
                            type: 'danger',
                            message: isResetingPassword
                                ? 'reset_password_token_expired'
                                : 'user_activation_token_expired',
                        });
                        if (!isResetingPassword) {
                            setShowResendActivationEmailBtn(true);
                        }
                    } else {
                        setIsTokenValid(true);
                    }
                    setIsValidatingToken(false);
                })
                .catch((error) => {
                    console.log({ error });
                    setAlert({
                        type: 'danger',
                        message: isResetingPassword
                            ? 'password_reset_fail'
                            : 'user_activation_fail',
                    });
                    setIsValidatingToken(false);
                });
        } else {
            setAlert({
                type: 'danger',
                message: isResetingPassword
                    ? 'reset_password_token_missing'
                    : 'user_activation_token_missing',
            });
            setIsValidatingToken(false);
        }
    }, [token]);

    useEffect(() => {
        setValidated(false);
    }, [password, confirmPassword]);

    return (
        <>
            <Header />
            <div className="reset-pass container">
                {alert && (
                    <Row className="mt-4 d-flex justify-content-center">
                        <Col md={6}>
                            <Alert
                                variant={alert.type}
                                className="mb-0 d-flex align-items-center justify-content-between"
                            >
                                <>
                                    {t(alert.message)}
                                    {showResendActivationEmailBtn && (
                                        <Button
                                            className="ms-2"
                                            variant="primary"
                                            onClick={resendUserActivationEmail}
                                        >
                                            {t('resend_activation_email')}
                                        </Button>
                                    )}
                                </>
                            </Alert>
                        </Col>
                    </Row>
                )}
                <Row className="mt-4 d-flex justify-content-center">
                    <Col md={6}>
                        <Card>
                            <Card.Header>
                                {t(isResetingPassword ? 'reset_password' : 'activate_new_user')}
                            </Card.Header>
                            <Card.Body>
                                {isValidatingToken ? (
                                    <div className="d-flex justify-content-center py-4">
                                        <Spinner></Spinner>
                                    </div>
                                ) : (
                                    <Form onSubmit={setNewPassword}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>{t('password')}</Form.Label>
                                            <Form.Control
                                                isInvalid={validated && passwordError}
                                                type="password"
                                                value={password}
                                                onChange={(event) =>
                                                    setPassword(event.target.value)
                                                }
                                            />
                                            {passwordError && (
                                                <Form.Control.Feedback type="invalid">
                                                    {t(passwordError)}
                                                </Form.Control.Feedback>
                                            )}
                                        </Form.Group>
                                        <Form.Group className="mb-3">
                                            <Form.Label>{t('confirm_password')}</Form.Label>
                                            <Form.Control
                                                isInvalid={validated && confirmPasswordError}
                                                type="password"
                                                value={confirmPassword}
                                                onChange={(event) =>
                                                    setConfirmPassword(event.target.value)
                                                }
                                            />
                                            {confirmPasswordError && (
                                                <Form.Control.Feedback type="invalid">
                                                    {t(confirmPasswordError)}
                                                </Form.Control.Feedback>
                                            )}
                                        </Form.Group>
                                        <div className="d-flex justify-content-between align-items-center">
                                            <Link to="/">{t('back_to_login')}</Link>
                                            <LoadingButton
                                                type="submit"
                                                isLoading={isLoading}
                                                maxWidth="auto"
                                                isDisabled={!isTokenValid || passwordReset}
                                            >
                                                {t(isResetingPassword ? 'save' : 'activate')}
                                            </LoadingButton>
                                        </div>
                                    </Form>
                                )}
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            </div>
        </>
    );
};

export default ResetPassword;
